import { useState, useEffect, useRef } from "react";
import dict from "../../../Data/dict.json";

import FLOAT_MENU from "../../common_trivial/FLOAT_MENU";

const Icon = () => {
  return (
    <svg
      style={{
        width: "16px",
        height: "16px",
      }}
      aria-hidden="true"
      fill="currentColor"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
    </svg>
  );
};

/**
 * Utility to escape regex special characters from user input
 * to prevent RegExp syntax errors.
 */
const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

/**
 * Helper function to group options by 'group' property and filter them
 * based on the item's name/label OR its keywords array.
 */
const groupAndFilterOptions = (array, keywords) => {
  let filteredArray = array;

  if (keywords) {
    const regex_string = `${keywords
      ?.split(" ")
      ?.filter(Boolean) // Prevents empty strings if user types double spaces
      ?.map((word) => `(?=.*?${escapeRegExp(word)})`) // Escaped to prevent crash
      ?.join("")}.*`;

    const regex_pattern = new RegExp(regex_string, "i");
    const lowerCaseKeywords = keywords.toLowerCase();

    filteredArray = array.filter((item) => {
      const label = item?.name || item?.label || "";
      const labelMatch = regex_pattern.test(String(label).toLowerCase());
      const keywordsMatch = item?.keywords?.some((k) =>
        String(k).toLowerCase().includes(lowerCaseKeywords),
      );

      return labelMatch || keywordsMatch;
    });
  }

  const grouped = filteredArray.reduce((acc, item) => {
    const groupName = item.group || "Ungrouped";
    if (!acc[groupName]) {
      acc[groupName] = [];
    }
    acc[groupName].push(item);
    return acc;
  }, {});

  const groupOrder = Array.from(
    new Set(array.map((item) => item.group || "Ungrouped")),
  ).filter((groupName) => grouped[groupName]);

  return groupOrder.map((groupName) => ({
    group: groupName,
    items: grouped[groupName],
  }));
};

const DROPDOWN_NEW = ({
  value: propValue,
  onChange,
  className = "",
  filter_on,
  placeholder,
  is_enable_empty,
  height,
  array_value = [],
  ...props
}) => {
  const language = localStorage?.language ? localStorage?.language : "ina";
  const defaultPlaceholder = placeholder || dict["Select"][language];

  const inputRef = useRef(null);
  const searchRef = useRef(null);
  const floatMenuRef = useRef(null);

  const [filtered_options, setFilteredOptions] = useState([]);
  const [is_active, setIsActive] = useState(false);
  const [value, setValue] = useState("");
  const [anchor, setAnchor] = useState(null);
  const [filterText, setFilterText] = useState("");

  useEffect(() => {
    const selected = propValue || "";
    setValue(selected);
    setFilteredOptions(groupAndFilterOptions(array_value, filterText));
    setIsActive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (is_active && searchRef?.current) {
      setTimeout(() => {
        searchRef?.current?.focus();
      }, 50);
    }
  }, [is_active]);

  useEffect(() => {
    if (propValue !== value) {
      setValue(propValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propValue]);

  useEffect(() => {
    setFilteredOptions(groupAndFilterOptions(array_value, filterText));
  }, [array_value, filterText]);

  useEffect(() => {
    const handler = (e) => {
      if (
        (inputRef.current && inputRef.current.contains(e.target)) ||
        (floatMenuRef.current && floatMenuRef.current.contains(e.target))
      ) {
        return;
      }
      setIsActive(false);
    };

    window.addEventListener("click", handler);
    return () => {
      window.removeEventListener("click", handler);
    };
  }, []);

  const filterOnChange = (e) => {
    const keywords = e?.target?.value?.toLowerCase();
    setFilterText(keywords);
  };

  const toggleDropdown = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (is_active) {
      setIsActive(false);
      setAnchor(null);
    } else {
      setFilterText("");

      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();

        // --- VIRTUAL ANCHOR WITH .contains() FIX ---
        const virtualAnchor = {
          getBoundingClientRect: () => rect,
          clientWidth: rect.width,
          clientHeight: rect.height,
          nodeType: 1, // Fake Element type

          // MOCK .contains() to prevent TypeError
          // We forward the check to the real inputRef DOM node
          contains: (target) => {
            return inputRef.current && inputRef.current.contains(target);
          },
        };

        setAnchor(virtualAnchor);
      }
      setIsActive(true);
    }
  };

  const handleClick = (key) => {
    const newValue = key || "";
    setValue(newValue);
    if (onChange) onChange(newValue);
    setIsActive(false);
  };

  const isSelected = (key) => {
    return value === key;
  };

  const options = (
    <>
      {is_enable_empty && (
        <div
          className={`badge cursor_pointer ${
            !value ? "green_container" : "background_grey_light"
          }`}
          onClick={() => {
            handleClick();
          }}
        >
          Select
        </div>
      )}

      {filtered_options?.length > 0 &&
        filtered_options.map((groupData, groupIdx) => {
          const isLastGroup = groupIdx === filtered_options.length - 1;

          return (
            <div key={groupIdx}>
              {groupData.group && groupData.items.length > 0 && (
                <p className="text_medium margin_top">{groupData.group}</p>
              )}

              {groupData.items.map((item, itemIdx) => {
                const itemValue = item?.value;
                // Added String() cast to protect against NaN bleeding into DOM
                const itemLabel = String(
                  item?.name || item?.label || item?.value || "",
                );
                const itemIcon = item?.icon;

                return (
                  <div
                    key={itemIdx}
                    onClick={() => {
                      handleClick(itemValue);
                    }}
                    data-mapid={`selectType-${groupIdx}-${itemIdx}`}
                    className="margin_bottom margin_top"
                  >
                    <div
                      className={`badge cursor_pointer ${
                        isSelected(itemValue)
                          ? "green_container"
                          : "background_grey_light"
                      }`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "17px",
                      }}
                    >
                      {itemIcon && (
                        <img
                          src={itemIcon}
                          alt={String(itemValue || "icon")}
                          style={{
                            width: "21px",
                            height: "21px",
                            objectFit: "contain",
                          }}
                        />
                      )}
                      <span>{itemLabel}</span>
                    </div>
                  </div>
                );
              })}

              {!isLastGroup && groupData.items.length > 0 && <hr />}
            </div>
          );
        })}

      {filterText && filtered_options.length === 0 && (
        <p className="text_small">No results found.</p>
      )}
    </>
  );

  const getDisplay = () => {
    if (!value) {
      return defaultPlaceholder;
    }
    const item = array_value?.find((opt) => opt?.value === value);
    // Added String() cast to protect against NaN bleeding into DOM
    const label = String(item?.name || item?.label || value || "");
    const icon = item?.icon;

    return (
      <span style={{ display: "flex", alignItems: "center", gap: "21px" }}>
        {icon && (
          <img
            src={icon}
            alt=""
            style={{
              width: "16px",
              height: "16px",
              objectFit: "contain",
            }}
          />
        )}
        <span>{label}</span>
      </span>
    );
  };

  let object_float = {};

  if (height) {
    object_float = { height, overflowY: "auto" };
  } else {
    object_float = { maxHeight: "80vh", overflowY: "auto" };
  }

  return (
    <main>
      <section
        {...props}
        className={`noselect background_white outline_grey button_big justify_between ${className}`}
        style={{
          display: "flex",
          alignItems: "center",
        }}
        onClick={(event) => toggleDropdown(event)}
        ref={inputRef}
      >
        <span className="dropdown_child">{getDisplay()}</span>
        <span className="flex">{<Icon />}</span>
      </section>

      {is_active && (
        <FLOAT_MENU anchorEl={anchor} open={is_active} onClose={toggleDropdown}>
          <div ref={floatMenuRef}>
            {filter_on && (
              <input
                type="text"
                ref={searchRef}
                onChange={filterOnChange}
                value={filterText}
                placeholder={dict["Search"][language] || "Search..."}
              />
            )}
            <div className="option_list_box_dropdown" style={object_float}>
              {options}
            </div>
          </div>
        </FLOAT_MENU>
      )}
    </main>
  );
};

export default DROPDOWN_NEW;
